import React, { useMemo, useRef, useState } from "react";

import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Stack,
  styled,
  TextField,
  Typography
} from "@mui/material";
import { ProgressOverlay, ValidationError } from "components";
import { Select } from "components/forms";
import { useFormik } from "formik";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError, useAuthActions } from "store";
import { AccountSignUpData } from "store/types";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import { getDomain } from "utils/auth";

const companyTypes = [
  "Asset manager",
  "Custodian",
  "Endowment",
  "Foundation",
  "Fund of Funds",
  "Insurance company",
  "Investment consultant",
  "Single family office",
  "Multi-family office",
  "RIA / Wealth management",
  "Secondaries fund",
  "Private pension plan",
  "Public pension plan",
  "Service provider"
];

type FormValues = Omit<AccountSignUpData, "invite">;

const InputsRow = styled(Stack)(() => ({
  flexDirection: "row",
  gap: "20px",
  ".MuiTextField-root": {
    flexGrow: 1,
    maxWidth: "250px",
    minWidth: "250px"
  }
}));

const apiDomain = getDomain();
const LOCALHOST = "localhost";

interface Props {
  initialValues: FormValues;
  token: string;
}

export const Form: React.FC<Props> = ({ initialValues, token }) => {
  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true);
  const [isRePasswordHidden, setIsRePasswordHidden] = useState<boolean>(true);
  const [inviteTokenErrors, setInviteTokenErrors] = useState<Array<string> | null>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);
  // const prevValueRef = useRef("");
  const { successModal } = useModals();

  const { signUpAccountAction } = useAuthActions();

  // ".altius.finance";
  const postfix: string = useMemo(
    () =>
      "." +
      apiDomain
        .split(".")
        .filter((_: string, index: number, array: string[]) => Boolean(index === array.length - 1 || index == array.length - 2))
        .join("."),
    []
  );

  const redirectToLogin = (domain: string) => {
    if (location.hostname === LOCALHOST) {
      successModal({
        msg: "If you hadn't been at localhost you would be redirected to log in to a newly created account."
      });
      return;
    }
    const link = document.createElement("a");
    link.href = `${import.meta.env.VITE_PROTOCOL}${domain}`;
    link.click();
  };

  const { action, loading } = useActionHook(signUpAccountAction, {
    onReject: (error: IApiServiceError) => {
      const { errors } = error;
      setErrors(apiErrorsToFormik(initialValues, errors));

      if (!errors.invite?.length) return;
      setInviteTokenErrors(errors.invite);
    },
    onSuccess: () => {
      redirectToLogin(`${values.domain}${postfix}`);
    }
  });

  const { handleChange, handleSubmit, setErrors, errors, dirty, isValid, values } = useFormik<FormValues>({
    initialValues: initialValues,
    onSubmit: ({ domain, ...restValues }) => action({ domain: `${domain}${postfix}`, ...restValues, invite: token }),
    validateOnChange: false
  });

  const handleChangeAndError: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    handleChange(event);
    const name = event.target.name as keyof FormValues;
    if (!errors[name]) return;
    const errorsCopy = errors;
    delete errorsCopy[name];
    setErrors(errorsCopy);
  };

  const handleSelectAndError = (event: SelectChangeEvent<unknown>) => {
    handleChange(event);
    const name = event.target.name as keyof FormValues;
    if (!errors[name]) return;
    const errorsCopy = errors;
    delete errorsCopy[name];
    setErrors(errorsCopy);
  };

  return (
    <ProgressOverlay disableOverlayBgc inProgress={loading}>
      <form onSubmit={handleSubmit}>
        <Stack gap="42px" width="520px">
          <Typography
            sx={{
              fontFamily: "Afacad",
              color: "#173C4C",
              fontSize: "40px",
              fontWeight: 400
            }}
          >
            Sign up
          </Typography>
          <Stack gap="20px">
            <InputsRow>
              <TextField
                error={Boolean(errors.first_name)}
                helperText={errors.first_name}
                value={values.first_name}
                onChange={handleChangeAndError}
                label="First Name"
                name="first_name"
                required
              />
              <TextField
                error={Boolean(errors.last_name)}
                helperText={errors.last_name}
                value={values.last_name}
                onChange={handleChangeAndError}
                label="Last Name"
                name="last_name"
                required
              />
            </InputsRow>

            <InputsRow>
              <TextField
                error={Boolean(errors.company_name)}
                helperText={errors.company_name}
                value={values.company_name}
                onChange={handleChangeAndError}
                label="Company Name"
                name="company_name"
                required
              />
              <Stack gap="8px" flexGrow={1}>
                <Box position="relative" width="250px">
                  <TextField
                    sx={{ width: "250px", zIndex: 1 }}
                    inputRef={domainInputRef}
                    error={Boolean(errors.domain)}
                    helperText={errors.domain}
                    value={values.domain}
                    onChange={(e) => {
                      const { value } = e.target;

                      // Limit with 25 symbols
                      if (value.length >= 25) return;

                      // Don't start with "-"
                      if (/^-/.test(value)) return;

                      // Let user enter only small letters and numbers
                      if (!/^[a-z,0-9,-]*$/.test(value)) return;

                      handleChangeAndError(e);
                    }}
                    label="Company Domain"
                    name="domain"
                    required
                  />
                  {/* Fake field to show full domain */}
                  <TextField
                    sx={{
                      position: "absolute",
                      left: 0,
                      width: "250px",
                      zIndex: 0
                    }}
                    disabled
                    value={values.domain ? `${values.domain}${postfix}` : ""}
                    label="Company Domain"
                  />
                </Box>

                <Typography sx={{ color: "#BAC7CE", fontWeight: 400, fontSize: "11px", lineHeight: 1 }}>
                  The address of your website.
                  <br />
                  Example: company_name.altius.capital
                </Typography>
              </Stack>
            </InputsRow>
            <InputsRow>
              <Select
                formProps={{ sx: { flexGrow: 1, borderRadius: "8px", ".MuiInputBase-root": { borderRadius: "8px" } } }}
                variant="outlined"
                label="Company Type"
                value={values.company_type}
                helperText={errors.company_type}
                error={Boolean(errors.company_type)}
                onChange={handleSelectAndError}
                name="company_type"
                items={companyTypes}
              />
              <TextField
                error={Boolean(errors.email)}
                helperText={errors.email}
                value={values.email}
                onChange={handleChangeAndError}
                label="Email"
                name="email"
                required
                sx={{ maxWidth: "250px" }}
              />
            </InputsRow>

            <InputsRow>
              <TextField
                error={Boolean(errors.password)}
                helperText={errors.password}
                value={values.password}
                onChange={handleChangeAndError}
                type={isPasswordHidden ? "password" : "text"}
                label="Password"
                name="password"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setIsPasswordHidden((prev) => !prev)} edge="end">
                        {isPasswordHidden ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                error={Boolean(errors.password_confirmation)}
                helperText={errors.password_confirmation}
                value={values.password_confirmation}
                onChange={handleChangeAndError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setIsRePasswordHidden((prev) => !prev)} edge="end">
                        {isRePasswordHidden ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                type={isRePasswordHidden ? "password" : "text"}
                label="Re-enter Password"
                name="password_confirmation"
                required
              />
            </InputsRow>
          </Stack>

          <Button
            disabled={!dirty || !isValid}
            type="submit"
            variant="contained"
            sx={{ width: "250px", borderRadius: "8px", height: "40px", bgcolor: "#060127" }}
          >
            Get Started
          </Button>
          <Stack>{inviteTokenErrors && <ValidationError messages={inviteTokenErrors} />}</Stack>
        </Stack>
      </form>
    </ProgressOverlay>
  );
};

{
  /* <TextField
      inputRef={domainInputRef}
      error={Boolean(errors.domain)}
      helperText={errors.domain}
      value={values.domain}
      onChange={handleChangeDomain}
      label="Company domain"
      name="domain"
      required
    /> */
}

// const handleChangeDomain: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
//   if (!domainInputRef.current) return;

//   // Get new value.
//   const newValue = event.target.value;

//   // Get new value without postfix.
//   const postfixIndex = newValue.length - postfix.length;
//   const newRootValue = newValue.endsWith(postfix) ? newValue.slice(0, postfixIndex) : newValue;

//   // Set empty value or value with postfix.
//   const isValueEmpty = !newRootValue?.length;
//   if (isValueEmpty) {
//     // setValue("");
//     event.target.value = "";
//     handleChangeAndError(event);
//   } else {
//     // Find selection positions.
//     const currentSelectionPosition = domainInputRef.current.selectionStart;
//     const newSelectionEndPosition = newRootValue.length;

//     // Detect if cursor at the end of the input but not a first character (which don't have postfix yet).
//     const isSelectionAtTheEnd = [
//       currentSelectionPosition,
//       newSelectionEndPosition,
//       newValue.length,
//       newRootValue.length
//     ].every((value, _, array) => value === array[0]);
//     const isSelectionAtTheEndNonFirstPosition = isSelectionAtTheEnd && currentSelectionPosition !== 1;

//     // Preserve cursor position if text is changed at the end of value.
//     if (currentSelectionPosition === newSelectionEndPosition && !isSelectionAtTheEndNonFirstPosition) {
//       setTimeout(
//         () => domainInputRef.current?.setSelectionRange(newSelectionEndPosition, newSelectionEndPosition),
//         0
//       );
//     }
//     // Put cursor at the end of value if text is changed at the postfix place.
//     else if (!isNull(currentSelectionPosition) && currentSelectionPosition >= postfixIndex) {
//       const newSelectionEndPosition = prevValueRef.current.length;
//       setTimeout(
//         () => domainInputRef.current?.setSelectionRange(newSelectionEndPosition, newSelectionEndPosition),
//         0
//       );
//       return; // do not change value if prefix is broken
//     }

//     // Don't start with "-"
//     if (/^-/.test(newRootValue)) return;

//     // Let user enter only small letters and numbers
//     if (!/^[a-z,0-9,-]*$/.test(newRootValue)) return;

//     const newFinalValue = `${newRootValue}${postfix}`;
//     // setValue(newFinalValue);
//     event.target.value = newFinalValue;
//     handleChangeAndError(event);
//     prevValueRef.current = newRootValue;
//   }
// };
